// frontend/src/app/shared/components/special-period-form/special-period-form.component.ts
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MerchantPeriod,
  MerchantPeriodService,
  CreatePeriodPayload,
  PeriodType,
  PERIOD_TYPE_META,
} from 'src/app/services/merchant-period.service';
import { LogService } from 'src/app/services/log.service';

@Component({
  selector: 'app-special-period-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './special-period-form.component.html',
})
export class SpecialPeriodFormComponent implements OnInit {
  /** Si fourni : mode édition */
  @Input() period: MerchantPeriod | null = null;

  @Output() saved = new EventEmitter<MerchantPeriod>();
  @Output() cancelled = new EventEmitter<void>();

  private periodService = inject(MerchantPeriodService);
  private log = inject(LogService);

  readonly PERIOD_TYPE_META = PERIOD_TYPE_META;
  readonly periodTypes = Object.keys(PERIOD_TYPE_META) as PeriodType[];

  // Form state
  label = '';
  type: PeriodType = 'vacation';
  startDate = '';
  endDate = '';
  color: string = '';
  notes = '';
  reductionCoeff: number | null = null;

  saving = signal(false);
  error = signal<string | null>(null);

  get isEditMode(): boolean {
    return this.period !== null;
  }

  get isCustomType(): boolean {
    return this.type === 'custom';
  }

  ngOnInit(): void {
    if (this.period) {
      this.label = this.period.label;
      this.type = this.period.type;
      this.startDate = this.period.startDate.slice(0, 10);
      this.endDate = this.period.endDate.slice(0, 10);
      this.color = this.period.color ?? '';
      this.notes = this.period.notes;
      this.reductionCoeff = this.period.reductionCoeff;
    } else {
      // Défaut : aujourd'hui + 7j
      const today = new Date();
      const next7 = new Date(today);
      next7.setDate(today.getDate() + 6);
      this.startDate = today.toISOString().slice(0, 10);
      this.endDate = next7.toISOString().slice(0, 10);
      this.onTypeChange();
    }
  }

  onTypeChange(): void {
    const meta = PERIOD_TYPE_META[this.type];
    // Always update color
    this.color = meta.defaultColor;
    // Label: auto-set from type unless custom (where user must type it)
    if (this.type !== 'custom') {
      this.label = meta.label;
    } else {
      // Clear label so user is forced to fill it
      this.label = '';
    }
  }

  async submit(): Promise<void> {
    this.error.set(null);

    if (!this.label.trim()) {
      this.error.set('Le libellé est requis.');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.error.set('Les dates sont requises.');
      return;
    }
    if (this.endDate < this.startDate) {
      this.error.set('La date de fin doit être après la date de début.');
      return;
    }

    const payload: CreatePeriodPayload = {
      label: this.label.trim(),
      type: this.type,
      startDate: this.startDate,
      endDate: this.endDate,
      color: this.color || null,
      notes: this.notes,
      reductionCoeff: this.reductionCoeff,
    };

    this.saving.set(true);
    const obs$ = this.isEditMode
      ? this.periodService.update(this.period!._id, payload)
      : this.periodService.create(payload);

    obs$.subscribe({
      next: (res) => {
        this.saving.set(false);
        this.log.info('Période sauvegardée', { id: res.period._id });
        this.saved.emit(res.period);
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Erreur lors de la sauvegarde.');
        this.log.error('Erreur sauvegarde période', { error: err });
      },
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
